import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CSSProperties, RefObject } from 'react'

import { useArtwork } from '~/api/artwork'
import type { CalendarEntry, FriendMark } from '~/api/types'

import { Faces } from '~/components/Faces'
import { useMarks } from '~/components/MarksContext'

import { AppLink } from '~/components/AppLink'

import s from './MonthGrid.module.css'
import { heldByStar, onList, spread, unspread, whoIs } from './views'
import { Bookmark } from '~/components/Bookmark'
import { Star } from '~/components/Star'

/**
 * The desktop month, ported from `_calendar_page.py`'s `renderMonth` and `chipRow`.
 *
 * Six weeks on one screen, each day a cell of chips. The whole point of the view is that a
 * month is one glance, so nothing here is allowed to push a row off the bottom: the grid is
 * a fixed height, the cells clip, and how many chips a cell shows is computed against the
 * height its row will actually get.
 *
 * The phone renders the same month as a grid of dots (`paintDots`), which is a different
 * component; this one is only ever drawn above 720px.
 */

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * What a chip costs in a month cell: two lines of it, or three when it carries a brand.
 *
 * Measured against the stylesheet — 11px/1.3 for the name, 8.5px/1.5 for the venue and for
 * the brand, plus the padding and the row's own margin. Both were two pixels short of what
 * the stylesheet actually produces, and two chips is all it takes for that to show: a plain
 * chip is 14.3 for the name plus 12.75 for the venue plus 5px of padding and 4px of row
 * margin — 36, not 34 — and a brand adds another 12.75. Rounded up rather than down,
 * because the cost of overestimating is one chip moving into "+N more" and the cost of
 * underestimating is a venue name printed on top of it, which is what the bottom of a busy
 * cell looked like.
 *
 * Re-measured when the venue inside a chip stopped being 8.5px mono caps and became a name
 * in the text face: a plain chip went from 36 to 39 and a branded one to 49.4, so 37 had
 * become an *under*-estimate — the failure this note describes, two pixels away. `.more`
 * went from 8.5px to the 10px floor at the same time, which is the other two pixels in
 * `CELL_CHROME`. Numbers read out of `getBoundingClientRect` on a full month, not adjusted
 * by eye.
 */
export const CHIP_PLAIN = 40
export const CHIP_BRANDED = 51

/** The date line and the "+N more" line, which every cell pays for whether or not it fills. */
export const CELL_CHROME = 46

/**
 * The same, for a cell drawn at a third height: a smaller date and less room around it.
 *
 * 22 was too low by half. Measured in Chrome once a past cell finally had a chip to hold:
 * the date line is 9, the "+N more" is 14, and the gaps and padding around them bring the
 * cell's own furniture to **35.4** — so a row sized as chrome-plus-one-chip overflowed by
 * 7.39px, which is the fault `CHIP_PLAIN`'s note describes one constant along. It never
 * showed before because a past cell was too short to draw a chip at all, so the number was
 * never asked to be right.
 */
export const SPENT_CHROME = 36

/** Four chips is the most a cell will show however tall its row is. */
const MAX_CHIPS = 4

/** `renderMonth`'s slack between the grid's height and the window's. */
const GRID_BLEED = 30

/** `chromeHeight()`'s own fallback for a header it cannot find, and `--chrome`'s in the CSS. */
/**
 * What a week that has ended is worth, against one you can still act on.
 *
 * A third. At that height a cell has room for its date and nothing else, which is the point:
 * the past is there to give the month its shape, and the shape is made of dates. What the
 * cell would have shown is still reachable — the count opens the day — and `.spent` drops
 * the chrome that a full cell needs so the date is not squeezed out too.
 */
const PAST_WEEK_SHARE = 1 / 3

/**
 * The least a spent week may be: its own chrome plus the tallest chip there is.
 *
 * A third of the screen came to 57px on an ordinary window, and 57 less `SPENT_CHROME` is a
 * 35px budget against a 40px chip — so the past showed counts and no gigs at all. The floor
 * is stated in the two numbers it depends on rather than as a figure, so re-measuring either
 * of them carries it.
 */
const PAST_FLOOR = SPENT_CHROME + CHIP_BRANDED

/**
 * The least a week you can still act on may be given: its chrome and two gigs.
 *
 * The past never takes height from the present. Raising a spent week to fit one card has to
 * come out of the live weeks — the grid is sized to the window on purpose, so every pixel a
 * past row gains is one a live row loses — and at an ordinary window height, giving the past
 * everything it wants left the current week showing a single gig a day. That is a worse
 * calendar than the one this set out to fix.
 */
const LIVE_FLOOR = CELL_CHROME + 2 * CHIP_PLAIN

const CHROME_FALLBACK = 220

export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

export interface MonthGridProps {
  entries: CalendarEntry[]
  /**
   * What each night would send with the cap lifted — `{iso: total}`. Not what was on the
   * night: one that has gone counts only the rows somebody claimed, because the payload
   * sends nothing else from it, and no depth setting brings the rest back.
   *
   * "+N more" is the only thing on this grid that says a night has more than it is showing,
   * and the cap is what makes the rows unable to say it: the payload sends a busy night a
   * fixed few unclaimed gigs and a cell fits about the same number, so N counted off the
   * rows is zero on exactly the nights with most behind them. Behind the reader there is
   * nothing more to show, which is why this number is small there rather than wrong — count
   * the whole night instead and the control appears over cells that have no chips.
   *
   * Optional, and it must be left out wherever this grid is not drawing the population the
   * numbers count. They count the payload whole; four of the five tabs filter it, and the
   * cap keeps every claimed row unconditionally, so on those tabs the rows in hand are
   * already the night and this number is somebody else's.
   */
  nights?: Record<string, number> | undefined
  /**
   * Today, as the server saw it — `YYYY-MM-DD`, London.
   *
   * This was derived here from the browser clock with a `Europe/London` formatter, in this
   * view and in two others. That is right nearly always and wrong exactly when it matters: a
   * machine a few minutes off, or a reader looking across London's midnight, gets a different
   * today from the one the calendar was built with. The night rule already cuts the day at
   * 05:00; "nearly right" is not a property this page can afford. The payload has carried it
   * all along.
   */
  today: string
  /** `YYYY-MM`, the month to draw. */
  month: string
  /** Nobody signed in is `null`, and it is what decides whether this view offers a decision. */
  me: Me | null
  /** `on` is the state being asked for, not the one being left. */
  onSave(id: string, on: boolean): void
  onGoing(id: string, on: boolean): void
  /** "+N more": the day the reader wants to see the whole of. */
  onDay(iso: string): void
  /**
   * Whether a night's rows are still on their way — the calendar's answer, from what it has
   * asked for and what has landed. A night it names is drawn with whatever has arrived and a
   * placeholder beside it, so it does not read as a night with nothing on. Left out, every
   * night is taken as arrived.
   */
  pending?: ((night: string) => boolean) | undefined
}

/* ------------------------------------------------------------------ the marks */

/*
 * Drawn, not typed. `♥` is a text glyph: at the 10px these sit at in a month cell the font
 * hints it into a wedge, and which wedge depends on the platform's emoji font. A path is the
 * same shape at every size and on every machine.
 */

function Tick({ fill }: { fill: boolean }) {
  return (
    <svg
      className={s.mark}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={fill ? 3.2 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12.5 9.5 18 20 6" />
    </svg>
  )
}

/* ------------------------------------------------------- what a chip has to say */

/**
 * `toggleSave` has always read `!(e.picked || e.liked)` — so on a row that was picked but
 * not personally hearted, the control drew itself empty and its first click *removed* it.
 * The month chip and the card said `liked`, the list row and the sheet said `picked`, and on
 * Curated — where everything is picked by definition — that showed as gigs on the list with
 * no heart on them.
 *
 * A gig is on the list two ways that are not a personal save: the editor added it, or a
 * starred act headlines it. Both are `picked`, and the heart is on for both.
 */


/**
 * And what the heart means is what the highlight means. `onList` was written to settle the
 * first half of that and only the marks were moved onto it: every view went on drawing its
 * highlight from `e.liked` alone, so a gig the editor added but nobody had personally
 * hearted came out with a filled heart and no colour on the row. Wilco at Hammersmith,
 * between two gigs that had both.
 */

function markOf(e: CalendarEntry, me: Me | null): 'going' | 'picked' | '' {
  return e.going ? 'going' : onList(e, me) ? 'picked' : ''
}

function short(value: string | null | undefined, n: number): string {
  const text = String(value ?? '')
  if (text.length <= n) return text
  // Break at a bill separator where there is one, so the result reads as names rather than a
  // word sliced in half.
  const cut = text.slice(0, n)
  const at = cut.lastIndexOf(' + ')
  return (at > n / 2 ? cut.slice(0, at) : cut.trimEnd()) + ' …'
}


function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ')
}

/* ------------------------------------------------------------------ the calendar */

/**
 * `startsAt` is UTC and the page is about London; the server sends `today` as a London date
 * for the same reason. There is no such field on `CalendarEntry`, so the day is read off the
 * London wall clock here, which agrees with the server except across a clock-skewed midnight.
 */


/**
 * The short month on a 1st, in UTC.
 *
 * The Python calls `toLocaleDateString` on a UTC-midnight date, which reads the machine's
 * own zone: west of Greenwich that is the evening of the 31st and prints the month before.
 * London is the only place the page is about and there it agrees, so nothing renders
 * differently — the zone is stated rather than assumed.
 */
const MON_SHORT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'short' })

/**
 * How tall a week is, given the room and which weeks have ended.
 *
 * Pure, and separated for that reason: this is the whole of a trade that can only otherwise
 * be seen by measuring Chrome at three window heights, and it is the sort of arithmetic that
 * goes wrong quietly. `MonthGrid` renders it; this decides it.
 *
 * Three rules, in order of who wins:
 *
 * 1. **A live week keeps two gigs a day.** The grid is sized to the window on purpose, so
 *    every pixel a spent row gains is one a live row loses. Giving the past everything it
 *    wanted left the current week showing a single gig at an ordinary window height, which
 *    is a worse calendar than the one this set out to fix.
 * 2. **A spent week gets enough for the tallest chip, if what is left allows it.** Measured
 *    at 813px it does not quite, and a past row lands at 80px — one plain chip rather than a
 *    branded one. At 1200px nothing is taken from anybody.
 * 3. **Never more than an even share**, so a month made entirely of past weeks cannot hand
 *    them more height than a week you can still act on would have had.
 */
export function rowHeights(room: number, spent: boolean[]): { past: number; live: number } {
  const pastWeeks = spent.filter(Boolean).length
  const liveWeeks = spent.length - pastWeeks
  const evenly = room / Math.max(spent.length, 1)
  if (!pastWeeks) return { past: 0, live: liveWeeks ? room / liveWeeks : 0 }

  const shares = pastWeeks * PAST_WEEK_SHARE + liveWeeks
  const wanted = Math.min(Math.max((room * PAST_WEEK_SHARE) / shares, PAST_FLOOR), evenly)
  const affordable = liveWeeks
    ? Math.max((room - liveWeeks * LIVE_FLOOR) / pastWeeks, SPENT_CHROME)
    : evenly
  const past = Math.min(wanted, affordable)
  return { past, live: liveWeeks ? (room - pastWeeks * past) / liveWeeks : 0 }
}


/**
 * Six weeks always covers a month, but the first or last of them can fall entirely in a
 * neighbouring one — a whole row of hatching that belongs to nobody. Weeks are built first
 * and any that touches no day of this month is dropped.
 */
export function weeksOf(month: string): Date[][] {
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7)) - 1
  const first = new Date(Date.UTC(year, index, 1))
  const startOffset = (first.getUTCDay() + 6) % 7 // Monday-first
  const start = new Date(first)
  start.setUTCDate(1 - startOffset)

  const weeks: Date[][] = []
  for (let w = 0; w < 6; w++) {
    const days: Date[] = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(start)
      d.setUTCDate(start.getUTCDate() + w * 7 + i)
      days.push(d)
    }
    if (days.some(d => d.getUTCMonth() === index)) weeks.push(days)
  }
  return weeks
}

/**
 * How many chips a cell can show is a question about the row it lands in, and the row's
 * height is the window's minus the header's. Cells clip, so guessing high hides events
 * behind no indication at all — "+N more" only tells the truth if the count is right.
 *
 * A chip is not one height. One with a brand above it — `EAT YOUR OWN EARS` over `Arab
 * Strap` over `HEAVEN` — is three lines where a plain one is two, and a cell of three
 * branded chips overflowed its row and clipped the "+N more" off the bottom. The count was
 * gone, so a day with forty gigs looked like a day with three. Each chip is measured for
 * what it actually is rather than all of them being assumed plain.
 */
/**
 * How many chips a cell can actually hold, which is sometimes none.
 *
 * It used to answer `Math.max(1, n)` — at least one, whatever the budget said. On a short
 * window that put a chip into a cell with no room for it: the artwork loaded, the row was
 * drawn, and the cell clipped it, so a reader got half a picture and a torn line of text.
 * Up to twenty-one cells a month at laptop heights.
 *
 * Zero is a real answer, and the cell already knows what to do with it: every event becomes
 * `+N more`, which is a control that opens them and is legible at any height. A count is
 * better than half a chip.
 */
function fits(list: CalendarEntry[], budget: number): number {
  let used = 0
  let n = 0
  for (const e of list) {
    used += e.festival ? CHIP_BRANDED : CHIP_PLAIN
    if (used > budget || n === MAX_CHIPS) break
    n += 1
  }
  return n
}

/**
 * The header's height, measured rather than assumed — `measureHeader` and `chromeHeight`,
 * which the Python ran on every render because there was no other moment to run them at.
 *
 * There is one here. A ResizeObserver fires when the header actually changes height, which
 * is what the re-measure was chasing: the tab row scrolls, the blurb appears with a venue,
 * and the month line wraps at some widths and not others. The window's own height is not
 * something a ResizeObserver reports, so a resize listener carries that half.
 */
function useChrome(grid: RefObject<HTMLDivElement | null>): {
  chrome: number
  viewport: number
} {
  const [chrome, setChrome] = useState(CHROME_FALLBACK)
  const [viewport, setViewport] = useState(() => window.innerHeight)

  useEffect(() => {
    const measure = () => {
      /*
       * The grid's own top edge, not a sum of the things above it.
       *
       * This used to measure `<header>`, which is the masthead — and the masthead is not
       * what is above the grid. The toolbar is: the month arrows, the mode switch, the five
       * tabs and the sentence under them, none of which are in a `<header>` and all of which
       * were unmeasured, so the bottom row of the month sat below the fold.
       *
       * Asking the element where it starts is right whatever ends up above it, and there
       * have been four different answers to that question in two days. It cannot recurse:
       * the grid is the last thing in the column, so nothing above it depends on its height.
       */
      const top = grid.current?.getBoundingClientRect().top
      setChrome(top === undefined ? CHROME_FALLBACK : top + window.scrollY)
      setViewport(window.innerHeight)
    }
    measure()
    window.addEventListener('resize', measure)
    // jsdom has no ResizeObserver and the suite runs there. The measurement is a refinement
    // of a working fallback, not a precondition for rendering, so its absence costs a test
    // the re-measure rather than the component.
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    // The whole column above it: the blurb wraps at some widths and the tab row scrolls, so
    // what is above the grid changes height without the window changing size.
    const column = grid.current?.parentElement
    if (watch && column) watch.observe(column)
    return () => {
      window.removeEventListener('resize', measure)
      watch?.disconnect()
    }
  }, [grid])

  return { chrome, viewport }
}

/**
 * "+N more", and what it is hiding.
 *
 * Hovering shows the rest of the night without leaving the month — the fastest way to answer
 * "what else is on" is not to navigate anywhere. Pressing goes to that day in the list,
 * which is the slower, more permanent answer.
 *
 * On the control rather than on the cell, deliberately: hovering any cell used to open a
 * panel, so crossing the grid to reach a chip meant one appearing over whatever you were
 * aiming at, including on days that had nothing hidden. The thing that says there is more is
 * the thing that shows it.
 *
 * Focus opens it too, because a keyboard has no hover and the rows inside are reachable.
 */
/** Where the panel goes: which side of the button, and how tall it may be. */
interface Placed {
  left: number
  width: number
  top?: number
  bottom?: number
  maxHeight: number
}

/** The gap between the button and the panel, and between the panel and the window's edge. */
const POP_GAP = 4
const POP_EDGE = 8

/** Narrower than this and the rows wrap into nonsense; a cell is often narrower than this. */
const POP_FLOOR = 240

/**
 * Above or below, whichever the window has room for.
 *
 * On the last row of a month, below is off the bottom of the screen — the panel opened into
 * space nobody can scroll to, so the control worked and what it opened was unreachable. The
 * same fault as the clip it was portalled out of to fix, one step further out.
 *
 * The height is capped to whichever side it lands on rather than assumed. Reading the CSS's
 * `max-height` back out in JavaScript would be the same value written twice, and it would
 * still be wrong on a short window; measuring the space and saying so cannot drift.
 */
function place(box: DOMRect): Placed {
  const below = window.innerHeight - box.bottom - POP_GAP - POP_EDGE
  const above = box.top - POP_GAP - POP_EDGE
  const up = above > below
  return {
    left: box.left,
    width: Math.max(POP_FLOOR, box.width),
    ...(up
      ? { bottom: window.innerHeight - box.top + POP_GAP }
      : { top: box.bottom + POP_GAP }),
    maxHeight: Math.max(up ? above : below, 0),
  }
}

function More({
  count,
  rest,
  me,
  onSave,
  onDay,
}: {
  /**
   * How many more are on the night — which is not `rest.length`, and is the number the
   * control exists to say. The payload holds a night's claimed rows plus a capped few, so
   * `rest` is what can be listed and `count` is what there is.
   */
  count: number
  rest: CalendarEntry[]
  me: Me | null
  onSave: (id: string, on: boolean) => void
  onDay: () => void
}) {
  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<Placed | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  // A grace period, so moving the pointer from the button onto the panel does not close it
  // between the two — they are siblings with a gap, and the gap is not a reason to give up.
  const leaving = useRef<number | undefined>(undefined)

  /*
   * The panel is drawn on the document, not inside the cell.
   *
   * A cell clips: `overflow: hidden` is what keeps a chip that does not fit from spilling
   * into the night below it. That clip took the panel with it, so the list of everything a
   * day holds was cut off at the cell's own edge — the control worked and what it opened
   * was unreadable. `z-index` cannot help, because a clip is not a stacking question, and
   * neither can `position: fixed`: the cell sets `backdrop-filter`, which makes it the
   * containing block for fixed descendants too.
   *
   * So it leaves. A portal puts it on `document.body`, and the button's own rectangle says
   * where to put it — measured at the moment it opens, which is why it closes on a scroll
   * rather than following one.
   *
   * The wrapper's two pointer handlers still cover it, which is the one thing about portals
   * that is easy to get wrong in both directions: React propagates a portal's events up the
   * *React* tree rather than the DOM one, so a pointer arriving on the panel reaches the
   * `<span>` it is written inside even though it is nowhere near it on the page. A second
   * pair of handlers on the panel was written first and deleted after the test showed
   * removing them changed nothing.
   */
  /*
   * What the panel would list. `count` is the night and `rest` is what the payload carried
   * past the chips, and the cap makes those two different numbers: a cell that fits
   * everything the wire sent has nothing left over, so the panel would be a heading over an
   * empty list under a button promising hundreds. A control that says there is more and then
   * shows none of it is worse than no control, so where there is nothing to list it stops
   * being a disclosure and is only what its click already does — the night itself.
   */
  const listable = rest.length > 0

  const hold = () => {
    if (!listable) return
    window.clearTimeout(leaving.current)
    const box = button.current?.getBoundingClientRect()
    if (box) setAt(place(box))
    setOpen(true)
  }
  const release = () => {
    leaving.current = window.setTimeout(() => setOpen(false), 180)
  }

  useEffect(() => {
    if (!open) return
    /*
     * The page moving underneath closes it; the panel moving inside itself does not.
     *
     * The listener is on capture, which is what lets it hear a scroll on any element rather
     * than only on the window — and that included the panel. So a busy night could be
     * opened and never read: the first turn of the wheel over the list closed the thing you
     * were turning it over. Reported twice, both times as "can't scroll".
     *
     * Measured once, so a page that really does move leaves the coordinates wrong and the
     * panel is closed rather than left floating.
     */
    const shut = (event: Event) => {
      const inside = event.target instanceof Node && panel.current?.contains(event.target)
      if (!inside) setOpen(false)
    }
    window.addEventListener('scroll', shut, true)
    window.addEventListener('resize', shut)
    return () => {
      window.removeEventListener('scroll', shut, true)
      window.removeEventListener('resize', shut)
    }
  }, [open])

  return (
    <span className={s.morewrap} onPointerEnter={hold} onPointerLeave={release}>
      <button
        type="button"
        className={s.more}
        ref={button}
        /* Absent, not `false`, when there is nothing behind it: a control that cannot
           expand should not tell a screen reader it is collapsed. */
        aria-expanded={listable ? open : undefined}
        onFocus={hold}
        onBlur={release}
        onClick={onDay}
      >
        +{count} more
      </button>

      {open && at
        ? createPortal(
        <div
          className={s.pop}
          ref={panel}
          style={{
            left: at.left,
            top: at.top,
            bottom: at.bottom,
            minWidth: at.width,
            maxHeight: at.maxHeight,
          }}
          onFocus={hold}
          onBlur={release}
        >
          {/* The same number the button says, because they name the same thing. The rows
              under it are what the payload happened to carry, which on a capped night is
              fewer — the panel is a shortcut into the night, not an inventory of it. */}
          <h4>{count} more</h4>
          {rest.map(e => (
            <div
              key={e.id}
              className={s.pl}
              /* The same sentence the cell's own chips carry. A row in here has no room for
                 the discs, so the hover is the only place it can be said. */
              title={e.friends?.length ? whoIs(e.friends, { past: e.past }) : undefined}
            >
              <AppLink className={s.plgo} to={`/e/${e.id}`} seed={unspread(e)}>
                <span className={s.t}>{e.time || '· ·'}</span>
                <span className={s.n}>
                  {short(e.title, 40)} <span className={s.at}>@ {short(e.venue, 20)}</span>
                </span>
              </AppLink>
              {/* A real button now that it is nobody's child. It was a `role="button"` span
                  with hand-written Enter and Space handling, which is what you have to write
                  when a control is nested inside another one. */}
              {me ? (
                <button
                  type="button"
                  className={s.mini}
                  aria-pressed={onList(e, me)}
                  title={onList(e, me) ? 'Curated' : 'Add to Curated'}
                  onClick={ev => {
                    ev.stopPropagation()
                    onSave(e.id, !onList(e, me))
                  }}
                >
                  <Bookmark fill={onList(e, me)} className={s.mark} />
                </button>
              ) : null}
            </div>
          ))}
        </div>,
            document.body,
          )
        : null}
    </span>
  )
}

/* ------------------------------------------------------------------------ a chip */

interface ChipRowProps {
  entry: CalendarEntry
  me: Me | null
  onSave(id: string, on: boolean): void
  onGoing(id: string, on: boolean): void
}

function ChipRow({ entry: e, me, onSave, onGoing }: ChipRowProps) {
  // The act the star is holding this by, if a star is holding it at all, and the action
  // that lets go. Read from context because no prop on this chip ever carried it.
  const held = heldByStar(e)
  const star = useMarks()?.star
  // Keyed by the URL, so a chip recycled onto a different gig by React does not inherit the
  // previous one's failure and hide artwork that loads perfectly well. The Python removed the
  // plate from the DOM in an `onerror` attribute; this is the same thing, undone on reuse.
  const { src: art, broken, onError } = useArtwork(e.image)

  const mark = markOf(e, me)
  const friends: FriendMark[] = e.friends ?? []
  const saved = onList(e, me)
  // How many faces the column holds. Three at the stack's overlap are as tall as a two-line
  // chip; a plate under three would be taller than the chip beside it, so where one is needed
  // the third face gives its place to it and the number covers the third as well.
  const going = friends.filter(f => f.going).length
  const faces = going > 3 ? 2 : 3

  return (
    <div className={s.chiprow}>
      {/*
       * `past` sits alongside the decision classes rather than replacing them: a gig you are
       * going to that happened this afternoon is still one you are going to.
       * Titles are truncated rather than left to the cell, because one 90-character festival
       * billing stretched a column and pulled the whole week out of shape.
       */}
      {/*
       * An anchor, not a button. `<main>` held 0 anchors and 146 buttons, so on the one page
       * built for browsing 7,229 listings you could not cmd-click a gig, middle-click it,
       * copy its link or drag it to a bookmark, and none of it was crawlable. A plain click
       * still opens the sheet over the grid — `AppLink` carries the page it was pressed on
       * for an `/e/` address, which is exactly what `onOpen` did by hand — and the browser
       * gets to do everything else it already knows how to do.
       *
       * The mark controls are a sibling span rather than children, so this is a link with no
       * interactive content inside it.
       */}
      <AppLink
        to={`/e/${e.id}`}
        // The row as the server sent it, not this night's "(Day n/N)" copy — see `unspread`.
        seed={unspread(e)}
        className={cx(
          s.chip,
          mark && s[mark],
          e.past && s.past,
          friends.length > 0 && s.friends,
          e.festival && s.festedge,
          e.lifecycle !== 'scheduled' && s.dead,
        )}
        /*
         * The gig, and who of your friends is going.
         *
         * The two discs beside this carry the same sentence, and a thirteen-pixel disc is a
         * target you have to already know is there — so the whole chip answers it too,
         * which is the width of the cell rather than the width of a full stop.
         */
        title={[
          `${e.festival ? `${e.festival} — ` : ''}${e.title} @ ${e.venue}`,
          friends.length > 0 ? whoIs(friends, { past: e.past }) : '',
        ]
          .filter(Boolean)
          .join(' — ')}
      >
        {e.image && !broken ? (
          <span className={s.thumb}>
            <img src={art ?? ''} alt="" loading="lazy" decoding="async" onError={onError} />
          </span>
        ) : null}
        {e.festival ? (
          <>
            <u>{short(e.festival, 26)}</u>
            {short(e.title, 28)}
          </>
        ) : (
          short(e.title, 30)
        )}
        {/*
          * The second line: normally the room, and for a festival the bill.
          *
          * A festival's venue *is* its own name — Reading Festival at Reading Festival — so
          * the line said the title again, twice on every night of the run. The acts are the
          * fact that changes and the one worth the space.
          *
          * Not the acts playing that *day*: no feed sends a per-day bill. Songkick's page
          * carries one flat list of 26 performers with no `subEvent` breakdown, so this is
          * who is playing across the weekend, and claiming otherwise would be inventing.
          */}
        <i>{e.lineup?.length ? short(e.lineup.join(', '), 34) : short(e.venue, 20)}</i>
      </AppLink>

      <span
        className={s.who}
        title={
          // Split on the flag, like the list and the sheet already do. This said "going"
          // about everybody, so a friend who had hearted a gig was reported as having said
          // they would be there — an invention rather than a leak, and the one thing the
          // two-sentence treatment in `WhosGoing` exists to prevent.
          friends.length > 0 ? whoIs(friends, { past: e.past }) : undefined
        }
      >
        {/*
         * The friends going, stacked down the column the way the card stacks them across,
         * and `+N` for the rest of the going. A save is not drawn here at all: a cell has
         * room for one fact per gig, and the fact is who will be there — the hover and the
         * sheet still say who saved it. The stack is handed every mark so its own sentence
         * agrees with the two titles around it, and told to draw the going only.
         */}
        <Faces
          friends={friends}
          only="going"
          max={faces}
          size={14}
          column
          past={e.past}
          to={`/e/${e.id}`}
        />
      </span>

      <span className={s.marks}>
        {/*
         * Nothing to press when there is nobody to press it. Every one of these controls
         * posts, and a signed-out reader's click ends at the login page — so the calendar was
         * offering a decision it could not take and then asking for an account to explain
         * itself. The slot is still emitted, so an absent first child does not shift the tick
         * up into the heart's place on the rows that have no reader.
         */}
        {/*
         * The star *and* the heart. They were mutually exclusive here too, so the one thing
         * you could not do to a followed act's gig was decide about it. The star is about
         * the act and every night they play; the heart is about this night.
         */}
        {me && held && star ? (
          <button
            type="button"
            className={s.mini}
            data-star={e.id}
            aria-pressed
            aria-label={`On the list because you follow ${held.name}. Unfollow them`}
            title={`On the list because you follow ${held.name}`}
            onClick={() => star(held.id, false)}
          >
            <Star fill className={s.mark} />
          </button>
        ) : null}
        {me ? (
          <button
            type="button"
            className={s.mini}
            data-heart={e.id}
            aria-pressed={saved}
            title={saved ? 'Curated' : 'Add to Curated'}
            onClick={() => onSave(e.id, !saved)}
          >
            <Bookmark fill={saved} className={s.mark} />
          </button>
        ) : (
          <span className={cx(s.mini, s.blank)} aria-hidden="true" />
        )}
        {me ? (
          <button
            type="button"
            className={s.mini}
            data-going={e.id}
            aria-pressed={e.going}
            title={
              e.past
                ? e.going
                  ? 'You were there'
                  : 'Mark that you were there'
                : e.going
                  ? "You're going"
                  : 'Mark as going'
            }
            onClick={() => onGoing(e.id, !e.going)}
          >
            <Tick fill={e.going} />
          </button>
        ) : null}
      </span>
    </div>
  )
}

/* --------------------------------------------------------------------- the month */

export function MonthGrid({
  entries,
  nights,
  month,
  me,
  today: todayIso,
  onSave,
  onGoing,
  onDay,
  pending,
}: MonthGridProps) {
  const grid = useRef<HTMLDivElement>(null)
  const { chrome, viewport } = useChrome(grid)

  // The month name, the arrows and "Nothing here yet." belong to whatever draws the toolbar
  // above this grid; with no month there is no grid to draw.
  if (!month) return null

  const index = Number(month.slice(5, 7)) - 1
  const weeks = weeksOf(month)
  const byDay = new Map<string, CalendarEntry[]>()
  // A run appears on every night of it, each chip saying which night. See `spread`: drawn
  // once on its opening night, a four-day festival left the other three showing nothing.
  for (const e of spread(entries)) {
    const day = byDay.get(e.iso)
    if (day) day.push(e)
    else byDay.set(e.iso, [e])
  }
  /*
   * A week that has ended gets half a row.
   *
   * It is still worth drawing — a month you can only see the future half of is not a month,
   * and the shape of the one you are in is most of what the grid is for — but it does not
   * need the same height as a week you can still act on. Six equal rows spend a third of the
   * screen on the past in the last week of a month.
   *
   * `past` is decided the same way `Entry.past` is: the night, not the minute. A week is
   * over when its Sunday is, and Sunday is over when the next night has begun.
   */
  const spent = weeks.map(week => {
    const sunday = week[week.length - 1]
    return sunday !== undefined && sunday.toISOString().slice(0, 10) < todayIso
  })
  const room = viewport - chrome - GRID_BLEED

  /*
   * A past week is short, and it was short enough to show nothing.
   *
   * Measured on a 813px window: 57px a row, less `SPENT_CHROME`, leaves a 35px budget — and
   * the cheapest chip costs 40. So every past week drew its dates and a "+N more" and not a
   * single gig, which reads as a week nothing happened in rather than one already spent.
   *
   * The share stays what it was; what is new is a floor under it. `PAST_FLOOR` is the
   * tallest chip plus that week's chrome, so *any* card fits rather than only a plain one —
   * a branded chip is three lines and is the one a festival week is made of.
   *
   * Capped at an even split as well, so a month of nothing but past weeks cannot hand them
   * more height than a week you can still act on would get. Between the two bounds the
   * original third is untouched.
   */
  const { past: pastRow, live: liveRow } = rowHeights(room, spent)
  const heightOf = (week: number) => (spent[week] ? pastRow : liveRow)

  const rows = `auto ${spent.map((_, week) => `${heightOf(week)}px`).join(' ')}`
  // The budget a cell has for chips, which is its own row's height less the chrome that row
  // pays for. Asked per week because the two kinds of week are no longer proportional.
  const budgetFor = (week: number) =>
    heightOf(week) - (spent[week] ? SPENT_CHROME : CELL_CHROME)

  // A month whose rows have not all arrived is not yet a month with nothing in it.
  const waiting = !!pending && weeks.some(week => week.some(d => pending(d.toISOString().slice(0, 10))))

  return (
    <>
      {entries.length === 0 && !waiting ? (
        /*
         * A month with nothing in it, said out loud.
         *
         * Six weeks of empty squares is what a cold visit used to get: the calendar opens on
         * the current month and the tab it opens on is Curated, so a reader arriving at the
         * end of August saw a grid with two thirds of it blank and no way to tell whether
         * that was the month, the filter, or the site being broken. The grid still draws —
         * the shape of the month is worth seeing — and this says which of the three it is.
         */
        <p className={s.empty}>
          Nothing on this month in this view. The month is still here; try{' '}
          <b>Everything</b>, or step forward.
        </p>
      ) : null}
      <div
        ref={grid}
        className={s.grid}
        style={
          {
            '--weeks': weeks.length,
            '--chrome': `${chrome}px`,
            gridTemplateRows: rows,
          } as CSSProperties
        }
      >
      {DOW.map(d => (
        <div key={d} className={s.dow}>
          {d}
        </div>
      ))}

      {weeks.flat().map((d, n) => {
        const week = Math.floor(n / 7)
        const iso = d.toISOString().slice(0, 10)
        const outside = d.getUTCMonth() !== index
        // The server's order, deliberately. It already ranks a night — going, then liked,
        // then picked, then by score — and re-sorting here would throw the score away and
        // put the clock in its place.
        const list = byDay.get(iso) ?? []
        const room = fits(list, budgetFor(week))
        /*
         * Against the night, not against the rows that arrived.
         *
         * `list.length` is what the payload sent, and the cap makes that fewer than the
         * night on a busy one — so subtracting it drew no control at all where the control
         * matters most. What is subtracted is what the *cell* drew, which on a cell too
         * short for a single chip is none of them.
         *
         * The larger of the two, because neither alone is a floor: `nights` counts a run
         * once under the night it opens while `spread` draws it on every night of it, so a
         * night in the middle of one has rows the count never saw — no key at all, or a key
         * counting only its own gigs. Taking `nights` there loses the run; taking the rows
         * loses the cap.
         */
        const onNight = Math.max(nights?.[iso] ?? 0, list.length)
        const hidden = onNight - room
        const due = !!pending?.(iso)

        return (
          <div
            key={iso}
            /* Absent rather than `false` on a night that has arrived, so the attribute alone
               says which cells are waiting. */
            aria-busy={due || undefined}
            className={cx(
              s.cell,
              outside && s.out,
              iso === todayIso && s.today,
              // A night behind us. `spent` already dims a whole *week* that is over; a
              // single past day inside the current week had nothing, so the first ten cells
              // of a month read exactly like the last twenty — and a reader scanning for
              // "what is left" had to find today first and work rightwards.
              iso < todayIso && s.gone,
              spent[week] && s.spent,
            )}
          >
            {/*
              * The day number opens the day. It looked pressable and was not: only
              * "+N more" opened a day, so a night with two gigs — where nothing is hidden
              * and no "+N more" is drawn — could not be opened from the month grid at all.
              */}
            <button
              type="button"
              className={s.dnum}
              /* `title` is only read as the name when a control has no text of its own, and
                 this one's text is a bare number — so a reader hears "21, button" and is
                 told nothing about where it goes. `aria-label` is the name; `title` is the
                 tooltip, and they say the same thing on purpose. */
              aria-label={`Everything on ${d.getUTCDate()} ${MON_SHORT.format(d)}`}
              title={`Everything on ${d.getUTCDate()} ${MON_SHORT.format(d)}`}
              onClick={() => onDay(iso)}
            >
              {d.getUTCDate()}
              {d.getUTCDate() === 1 ? ` ${MON_SHORT.format(d)}` : ''}
            </button>

            {list.slice(0, room).map(e => (
              <ChipRow
                key={e.id}
                entry={e}
                me={me}
                onSave={onSave}
                onGoing={onGoing}
              />
            ))}

            {/* Beside the chips already drawn, never over them: the rows still to come go
                where it stands. No text — the cell's date is all the cell says. */}
            {due ? <span className={s.pending} aria-hidden="true" /> : null}

            {/*
             * On "+N more", not on the cell.
             *
             * Hovering any cell opened the day, so crossing the grid to reach a chip meant a
             * panel appearing over whatever you were aiming at — including on days whose
             * events were all already visible, where it had nothing to add. The control that
             * says there is more is the one that shows it, and it names exactly how many.
             */}
            {hidden > 0 ? (
              <More
                count={hidden}
                rest={list.slice(room)}
                me={me}
                onSave={onSave}
                onDay={() => onDay(iso)}
              />
            ) : null}
          </div>
        )
      })}
      </div>
    </>
  )
}
